import { userManager, keycloakConfig } from '../config/keycloak';
import { User } from 'oidc-client-ts';

export const keycloakService = {
  /**
   * Initialize Keycloak and check if user is already authenticated
   */
  init: async (): Promise<User | null> => {
    try {
      const user = await userManager.getUser();
      return user;
    } catch (error) {
      // Silently fail if Keycloak is not available (for development)
      console.warn('Keycloak initialization failed (this is OK if Keycloak is not running):', error);
      return null;
    }
  },

  /**
   * Keycloak user self-registration (realm must allow registrations).
   * Full-page navigation — completes via the same SPA /callback redirect_uri as login.
   */
  getRegistrationUrl: (): string => {
    const base = `${keycloakConfig.authority}/protocol/openid-connect/registrations`;
    const params = new URLSearchParams({
      client_id: String(keycloakConfig.client_id ?? ''),
      response_type: 'code',
      scope: keycloakConfig.scope ?? 'openid profile email offline_access',
      redirect_uri: String(keycloakConfig.redirect_uri ?? `${window.location.origin}/callback`),
    });
    sessionStorage.setItem('keycloak_redirect_path', '/posts/new');
    localStorage.setItem('keycloak_redirect_path', '/posts/new');
    return `${base}?${params.toString()}`;
  },

  /**
   * Login - redirect to Keycloak login page
   */
  login: async (redirectPath?: string): Promise<void> => {
    try {
      // Store redirect path in both sessionStorage and localStorage as backup
      // (localStorage persists better across redirects)
      if (redirectPath) {
        console.log('keycloakService.login: Storing redirect path:', redirectPath);
        sessionStorage.setItem('keycloak_redirect_path', redirectPath);
        localStorage.setItem('keycloak_redirect_path', redirectPath);
      } else {
        console.log('keycloakService.login: No redirect path provided');
      }
      await userManager.signinRedirect();
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  },

  /**
   * Build a Keycloak sign-in URL without navigating. Caller is responsible
   * for opening it (typically via a synchronous window.open inside a click
   * handler so popup blockers stay out of the way). Optional redirectPath is
   * stored so the OIDC callback page can route the user back, although in
   * the cross-tab flow the new tab simply closes itself.
   */
  buildLoginUrl: async (redirectPath?: string): Promise<string> => {
    if (redirectPath) {
      sessionStorage.setItem('keycloak_redirect_path', redirectPath);
      localStorage.setItem('keycloak_redirect_path', redirectPath);
    }
    const request = await userManager.createSigninRequest({});
    return request.url;
  },

  /**
   * Logout
   */
  logout: async (): Promise<void> => {
    try {
      await userManager.signoutRedirect();
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  },

  /**
   * Handle OIDC callback
   */
  handleCallback: async (): Promise<User> => {
    try {
      const user = await userManager.signinRedirectCallback();
      if (!user || !user.access_token) {
        throw new Error('Failed to obtain access token from Keycloak');
      }
      return user;
    } catch (error: any) {
      // Log detailed error information
      console.error('Error handling callback:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.error) {
        console.error('Error type:', error.error);
      }
      if (error.error_description) {
        console.error('Error description:', error.error_description);
      }
      // Clear any stale user data
      await userManager.removeUser();
      throw error;
    }
  },

  /**
   * Get current user
   */
  getUser: async (): Promise<User | null> => {
    try {
      return await userManager.getUser();
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: async (): Promise<boolean> => {
    const user = await userManager.getUser();
    return user !== null && !user.expired;
  },

  /**
   * Get access token
   */
  getAccessToken: async (): Promise<string | null> => {
    const user = await userManager.getUser();
    return user?.access_token || null;
  },

  /**
   * Refresh token to get latest user info and roles
   */
  refreshToken: async (): Promise<User | null> => {
    try {
      const user = await userManager.getUser();
      if (user && user.expired) {
        // Token expired, do silent refresh
        return await userManager.signinSilent();
      } else if (user) {
        // Token still valid, but force refresh to get latest roles
        return await userManager.signinSilent();
      }
      return user;
    } catch (error) {
      console.warn('Token refresh failed (may need to re-login):', error);
      return null;
    }
  },
};

